import React, { useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { AnimatePresence } from "framer-motion"
import moment from 'moment-timezone'
import { 
    EditorState, 
    ContentState,
    convertToRaw
} from 'draft-js';

import { Animation, TextField, ListTile } from 'components'
import { FaSearch } from 'react-icons/fa'
import { ItemBlock } from '../../components'

import { itemContext, moduleContext } from 'context'

const Container = styled.div`
    background-color: var(--primary-color);
    border-radius: 5px;
    width: 100%;
    cursor: auto;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    top: 1px;
    box-shadow: 0px 2px 1px -1px var(--primary-accent2-color),
                0px 1px 1px 0px var(--primary-accent2-color), 
                0px 1px 3px 0px var(--primary-accent2-color);
`

const Header = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-column-gap: 0.5rem;
    align-items: center;
    border-bottom: ${props => props.open && '1px solid var(--secondary-color)'};
    padding-bottom: ${props => props.open && '0.5rem'};
    padding: 0.5rem;
`

const Body = styled.div`
    padding: 1rem 0.5rem;
    width: 100%;
    height: min-content;
    display: flex;
    flex-direction: column;
    background-color: var('--secondary-color');
    overflow-y: auto;
    padding: 0.5rem;
`

const Title = styled.div`
    font-weight: bolder;
    font-size: 0.8rem;
    padding: 0.5rem 0.5rem 0 0.5rem;
    color: var(--secondary-accent-color);
`

const moduleStyle = {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    border: '2px solid transparent'
}

const selectedModuleStyle = {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    border: '2px solid var(--focus-color)'
}

const CommandPreview = props => {
    const {
        progress
    } = props
    const [value, setValue] = React.useState('')
    const [blocks, setBlocks] = React.useState([])
    const timezone = moment.tz.guess();

    ///////////
    // Pages //
    ///////////
    const page = {
        page_name: 'Test page',
        icon: {}
    }

    const thirdPartyApps = {
        page_name: 'Third-party apps',
        icon: { type: 'emoji', value: "spock-hand" }
    }

    const portalPage = {
        page_name: 'Portal',
        icon: { type: 'emoji', value: "globe_with_meridians" }
    }

    const opsPages = {
        page_name: 'Reg Ops',
        icon: { type: 'emoji', value: 'gear' }
    }

    const roadmapPage = {
        page_name: 'Roadmap',
        icon: { type: 'emoji', value: 'car' }
    }

    const lifePage = {
        page_name: 'Life',
        icon: { type: 'emoji', value: 'rainbow' }
    }

    /////////////////
    // Controllers //
    /////////////////
    const getItem = (key, item) => {
        let updatedItem = Object.assign({}, {
            content: '',
            priority: itemContext.PRIORITY.NONE,
            status: itemContext.STATUS.INCOMPLETE,
            offset: 0
        }, {...item})
        updatedItem['content'] = JSON.stringify(convertToRaw(EditorState.createWithContent(ContentState.createFromText(item['content'])).getCurrentContent()))
        return (
            <ItemBlock type='search' itemID={updatedItem['itemID']} key={key}/>
        )
    }

    const values = {
        'Integration': {blocks: [], upper: 0.33, mid: 0.165, lower: 0, 
            items: [
                <Title key='schedule_title'>Items</Title>,
                getItem('search1',{
                    content: 'Revamped design for integrations feature',
                    page: roadmapPage,
                    children: {completed: 0, total: 2}, 
                    start_date: moment().tz(timezone),
                    end_date: moment().tz(timezone),
                    start_time: moment().tz(timezone).set({hour: 14, minute: 0}),
                    end_time: moment().tz(timezone).set({hour: 16, minute: 0})
                }),
                getItem('search2',{
                    content: 'Google integrations', 
                    page: thirdPartyApps,
                    children: {completed: 0, total: 3},
                }),
                getItem('search3',{
                    content: 'Zoom integration', 
                    page: thirdPartyApps,
                }),
                getItem('search4',{
                    content: 'Integration of portal API with security features', 
                    tags: [{content: 'API', color: '#f57600'}], 
                    page: portalPage,
                    children: {completed: 2, total: 7},
                    start_date: moment().tz(timezone),
                    end_date: moment().tz(timezone).add({day: 10}),
                }),
                getItem('search4',{
                    content: 'Issues with OTP integration for change phone flow', 
                    page: portalPage,
                    tags: [{content: 'Bug', color: '#ea4861'}],
                })
            ]},
        'Today': {blocks: [], upper: 0.66, mid: 0.495, lower: 0.33, 
                items: [
                    <Title key='schedule_title'>Modules</Title>,
                    <ListTile text={`Upcoming on ${moment().tz(timezone).format('ddd, d-MMM')}`} icon={moduleContext.ICONS['CALENDAR']}
                        styles={progress > 0.55 ? selectedModuleStyle : moduleStyle}
                        onSelect={() => {}}/>,
                    <Title key='schedule_title'>Scheduled</Title>,
                    getItem('today1',{
                        content: 'Prepare slides for weekly update', 
                        priority: itemContext.PRIORITY.MEDIUM,
                        page: opsPages,
                        start_date: moment().tz(timezone),
                        end_date: moment().tz(timezone),
                        start_time: moment().tz(timezone).set({hour: 8, minute: 0}),
                        end_time: moment().tz(timezone).set({hour: 9, minute: 0})
                    }),
    
                    getItem('today2',{
                        content: 'Meeting with John on Q2 priorities', 
                        priority: itemContext.PRIORITY.LOW,
                        page: roadmapPage,
                        start_date: moment().tz(timezone),
                        end_date: moment().tz(timezone),
                        start_time: moment().tz(timezone).set({hour: 11, minute: 0}),
                        end_time: moment().tz(timezone).set({hour: 12, minute: 0})
                    }),
    
                    getItem('today3',{
                        content: 'Revamped design for integrations feature', 
                        page: roadmapPage,
                        start_date: moment().tz(timezone),
                        end_date: moment().tz(timezone),
                        start_time: moment().tz(timezone).set({hour: 14, minute: 0}),
                        end_time: moment().tz(timezone).set({hour: 16, minute: 0})
                    }),
    
                    getItem('today4',{
                        content: 'Breakfast with Jane', 
                        status: itemContext.STATUS.COMPLETED,
                        page: lifePage,
                        start_date: moment().tz(timezone),
                        end_date: moment().tz(timezone),
                        start_time: moment().tz(timezone).set({hour: 7, minute: 0}),
                        end_time: moment().tz(timezone).set({hour: 8, minute: 0})
                    }),
    
                ]},
        '#bugs': {blocks: [], upper: 1, mid: 0.825, lower: 0.66, 
            items: [
                <Title key='schedule_title'>Items</Title>,
                getItem('bugs1',{
                    content: 'Sign out button not working', 
                    priority: itemContext.PRIORITY.HIGH,
                    tags: [{content: 'Bug', color: '#ea4861'}], 
                    page: roadmapPage,
                    start_date: moment().tz(timezone),
                    end_date: moment().tz(timezone),
                }),
                getItem('bugs2',{
                    content: 'Rate limit not supported on app v2.65', 
                    tags: [{content: 'Bug', color: '#ea4861'}, {content: 'API', color: '#f57600'}], 
                    priority: itemContext.PRIORITY.MEDIUM,
                    page: roadmapPage,
                    children: {completed: 0, total: 2}, 
                    start_date: moment().tz(timezone),
                    end_date: moment().tz(timezone),
                }),
                getItem('bugs3',{
                    content: 'Wrong translation for change email warning', 
                    tags: [{content: 'Bug', color: '#ea4861'}], 
                    page: portalPage
                }),
            ]},
    }
    useEffect(() => {
        // Search value
        const keyword = Object.keys(values)[progress < 0.33 ? 0 : progress < 0.66 ? 1 : 2]
        const upper = values[keyword]['upper']
        const mid = values[keyword]['mid']
        const lower = values[keyword]['lower']
        let y = progress > mid ? progress - mid : progress - lower
        const n = y < 0.01 ? 0 : Math.ceil((y/0.165)*keyword.length)
        
        const newValue = progress > 0.825 ? keyword
                        : progress > mid ? keyword.slice(0, keyword.length - n) 
                        : keyword.slice(0, n)
        setValue(newValue)
        
        // Blocks
        progress > mid ? setBlocks(values[keyword]['items']) : setBlocks([])
    }, [progress])
    

    return (
        <Container>
            {/* {progress} */}
            <Header open={blocks.length > 0}>
                <FaSearch style={{color: 'var(--secondary-accent-color)', fontSize: '1rem'}}/>
                <TextField key={value} type='single' focus={value.length > 0} disabled
                    value={value} placeholder='Search...' style={{width: '100%'}}
                    onChange={() => {}} onKeyDown={() => {}} />
            </Header>
            <AnimatePresence initial={false}>
                {blocks.length > 0 &&
                <Animation animation='expand'>
                    <Body>{blocks}</Body>
                </Animation>}
            </AnimatePresence>
        </Container>
    )
}

CommandPreview.propTypes = {
    progress: PropTypes.number
}

export default CommandPreview